import { alongTrackM, crossTrackM, NM_TO_M, type AirportData, type RunwayEnd } from '@atc/core';
import type { Aircraft } from './aircraft';
import type { Instruction } from './commands';

/** Physical runway identity includes both directions; crossings share their exclusion zone. */
export function runwayKey(airport: AirportData, end: RunwayEnd): string {
  return airport.runways.find((r) => r.ends.some((e) => e.name === end.name))?.name ?? end.name;
}

export function runwaysIntersect(airport: AirportData, a: RunwayEnd, b: RunwayEnd): boolean {
  const ra = airport.runways.find((r) => r.ends.some((e) => e.name === a.name));
  const rb = airport.runways.find((r) => r.ends.some((e) => e.name === b.name));
  if (!ra || !rb) return false;
  if (ra === rb) return true;
  const side = (p: RunwayEnd, q: RunwayEnd, r: RunwayEnd) => (q.lon - p.lon) * (r.lat - p.lat) - (q.lat - p.lat) * (r.lon - p.lon);
  const [p, q] = ra.ends, [r, s] = rb.ends;
  const overlaps = Math.max(Math.min(p.lat, q.lat), Math.min(r.lat, s.lat)) <= Math.min(Math.max(p.lat, q.lat), Math.max(r.lat, s.lat))
    && Math.max(Math.min(p.lon, q.lon), Math.min(r.lon, s.lon)) <= Math.min(Math.max(p.lon, q.lon), Math.max(r.lon, s.lon));
  return overlaps && side(p, q, r) * side(p, q, s) <= 0 && side(r, s, p) * side(r, s, q) <= 0;
}

export function occupiedEnd(ac: Aircraft, airport?: AirportData): RunwayEnd | undefined {
  if (ac.control && ac.pilot.pending.some((i) => i.kind === 'lineUp' || i.kind === 'takeoff')) return ac.departureRunway;
  if (ac.crossing?.approved && airport) return airport.runways.flatMap((r) => r.ends).find((e) => e.name === ac.crossing!.runway);
  if (['lineUp', 'linedUp', 'takeoffRoll', 'abortRoll'].includes(ac.phase)) return ac.departureRunway;
  if (ac.phase === 'rollout' || ac.phase === 'vacating') return ac.approach?.runway;
  // Keep the departure exclusion until the aircraft clears the runway end or climbs 500 ft.
  if (ac.phase === 'departure' && ac.departureRunway && ac.altitudeFt < ac.departureRunway.elevationM / 0.3048 + 500) {
    const distance = alongTrackM(ac.departureRunway.lat, ac.departureRunway.lon, ac.departureRunway.headingDeg, ac.lat, ac.lon);
    const length = airport?.runways.find((r) => r.ends.some((e) => e.name === ac.departureRunway!.name))?.lengthM ?? Infinity;
    if (distance < length) return ac.departureRunway;
  }
  return undefined;
}

export function shortFinalEnd(ac: Aircraft): RunwayEnd | undefined {
  const rw = ac.approach?.runway;
  if (!rw || ac.onGround || !ac.approach?.established) return undefined;
  const back = -alongTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon);
  // Protect the flare beyond the threshold as well; touchdown is not at the threshold point.
  return back > -1000 && back < 4 * NM_TO_M && Math.abs(crossTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon)) < 0.5 * NM_TO_M ? rw : undefined;
}

export function runwayUnable(airport: AirportData, active: readonly Aircraft[], ac: Aircraft, instr: Instruction): string | null {
  if (instr.kind !== 'takeoff' && instr.kind !== 'lineUp' && instr.kind !== 'land') return null;
  const rw = instr.kind === 'land' ? airport.runways.flatMap((r) => r.ends).find((e) => e.name === instr.runway) : ac.departureRunway;
  if (!rw) return null; // normal instruction validation reports an unknown runway
  if (instr.kind === 'takeoff' || instr.kind === 'lineUp') {
    const ownDistance = distanceFromThreshold(ac, rw);
    const ahead = active.find((other) => other !== ac && other.departureRunway?.name === rw.name &&
      ['taxiOut', 'holdShort', 'lineUp', 'linedUp', 'takeoffRoll', 'abortRoll'].includes(other.phase) &&
      distanceFromThreshold(other, rw) + 25 < ownDistance);
    if (ahead) return ['lineUp', 'linedUp', 'takeoffRoll', 'abortRoll'].includes(ahead.phase)
      ? `runway ${rw.name} occupied by ${ahead.callsign}`
      : `runway ${rw.name} departure queue ahead: ${ahead.callsign}`;
  }
  // Long-final landing clearances can coexist with a departure; final safety is checked every step.
  if (instr.kind === 'land' && -alongTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon) > 4 * NM_TO_M) return null;
  for (const other of active) {
    if (other === ac) continue;
    const end = occupiedEnd(other, airport);
    if (end && runwaysIntersect(airport, rw, end)) return `runway ${rw.name} occupied by ${other.callsign}`;
    const final = shortFinalEnd(other);
    if (final && (runwaysIntersect(airport, rw, final) || runwayKey(airport, rw) === runwayKey(airport, final))) return `${other.callsign} on short final runway ${final.name}`;
  }
  return null;
}

function distanceFromThreshold(ac: Aircraft, runway: RunwayEnd): number {
  const point = ac.holdShortPos ?? ac;
  return Math.hypot((point.lat - runway.lat) * 111_320, (point.lon - runway.lon) * 111_320 * Math.cos(runway.lat * Math.PI / 180));
}

export function occupiedRunways(airport: AirportData, active: readonly Aircraft[]): Set<string> {
  const ends = active.map((ac) => occupiedEnd(ac, airport)).filter((e): e is RunwayEnd => !!e);
  return new Set(airport.runways.filter((r) => ends.some((e) => runwaysIntersect(airport, r.ends[0], e))).map((r) => r.name));
}

export function independentApproach(airport: AirportData | undefined, a: Aircraft, b: Aircraft): boolean {
  const aa = a.approach, bb = b.approach;
  if (!airport || !aa?.established || !bb?.established) return false;
  return (airport.xAtc.independentApproaches ?? []).some(([x, y]) => (aa.runway.name === x && bb.runway.name === y) || (aa.runway.name === y && bb.runway.name === x));
}
